// GSTIN state codes per CBIC — first 2 digits of a GSTIN identify the
// registered state/UT. Used to determine CGST+SGST (intra-state) vs
// IGST (inter-state) on invoices.
export const GST_STATE_CODES = {
  "01": "Jammu & Kashmir",
  "02": "Himachal Pradesh",
  "03": "Punjab",
  "04": "Chandigarh",
  "05": "Uttarakhand",
  "06": "Haryana",
  "07": "Delhi",
  "08": "Rajasthan",
  "09": "Uttar Pradesh",
  10: "Bihar",
  11: "Sikkim",
  12: "Arunachal Pradesh",
  13: "Nagaland",
  14: "Manipur",
  15: "Mizoram",
  16: "Tripura",
  17: "Meghalaya",
  18: "Assam",
  19: "West Bengal",
  20: "Jharkhand",
  21: "Odisha",
  22: "Chhattisgarh",
  23: "Madhya Pradesh",
  24: "Gujarat",
  25: "Daman & Diu",
  26: "Dadra & Nagar Haveli",
  27: "Maharashtra",
  28: "Andhra Pradesh (Old)",
  29: "Karnataka",
  30: "Goa",
  31: "Lakshadweep",
  32: "Kerala",
  33: "Tamil Nadu",
  34: "Puducherry",
  35: "Andaman & Nicobar Islands",
  36: "Telangana",
  37: "Andhra Pradesh",
  38: "Ladakh",
};

export function stateFromGSTIN(gstin) {
  const code = (gstin || "").trim().substring(0, 2);
  return GST_STATE_CODES[code] || null;
}

function normalizeStateName(state) {
  return (state || "").trim().toLowerCase();
}

// Reverse-match a free-text state name (e.g. from Customer.state) against
// the known GSTIN state list, so unregistered (no-GSTIN) customers can
// still be placed correctly for CGST/SGST vs IGST purposes.
export function findKnownState(stateName) {
  const target = normalizeStateName(stateName);
  if (!target) return null;

  const match = Object.values(GST_STATE_CODES).find(
    (name) => normalizeStateName(name) === target
  );
  return match || null;
}

// Priority: seller's GSTIN prefix -> seller's free-text state field, so
// businesses without a GSTIN can still be placed for CGST/SGST vs IGST
// purposes, as long as they've set a state on their business profile.
export function resolveSellerState({ sellerGstin, sellerState } = {}) {
  const fromGstin = stateFromGSTIN(sellerGstin);
  if (fromGstin) return fromGstin;

  return findKnownState(sellerState);
}

// Priority: explicit place-of-supply string (if it resolves to a known
// state) -> customer GSTIN prefix -> customer's free-text state field.
export function resolvePlaceOfSupplyState({ customerGstin, customerState, explicitPlaceOfSupply } = {}) {
  const fromExplicit = findKnownState(explicitPlaceOfSupply);
  if (fromExplicit) return fromExplicit;

  const fromGstin = stateFromGSTIN(customerGstin);
  if (fromGstin) return fromGstin;

  const fromCustomerState = findKnownState(customerState);
  if (fromCustomerState) return fromCustomerState;

  return null;
}

// Splits a line's tax amount into CGST+SGST (intra-state) or IGST
// (inter-state), based on comparing seller state vs place of supply.
export function splitTax({ sellerState, placeOfSupplyState, taxableAmount, taxPercent }) {
  const amount = Math.max(0, Number(taxableAmount) || 0);
  const percent = Math.max(0, Number(taxPercent) || 0);
  const totalTax = (amount * percent) / 100;

  const isInterState = Boolean(
    sellerState && placeOfSupplyState && sellerState !== placeOfSupplyState
  );

  if (isInterState) {
    return { isInterState: true, cgst: 0, sgst: 0, igst: totalTax };
  }

  const half = totalTax / 2;
  return { isInterState: false, cgst: half, sgst: half, igst: 0 };
}

export function formatINR(value) {
  if (value === null || value === undefined) return "0";

  const num = Number(value);

  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function numberToWords(num) {
  if (!num) return "Zero";

  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const inWords = (n) => {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n / 10)] + " " + a[n % 10];
    if (n < 1000)
      return a[Math.floor(n / 100)] + " Hundred " + inWords(n % 100);
    if (n < 100000)
      return (
        inWords(Math.floor(n / 1000)) + " Thousand " + inWords(n % 1000)
      );
    if (n < 10000000)
      return (
        inWords(Math.floor(n / 100000)) + " Lakh " + inWords(n % 100000)
      );
    return (
      inWords(Math.floor(n / 10000000)) + " Crore " + inWords(n % 10000000)
    );
  };

  return inWords(Math.floor(num)).trim() + " Only";
}
